import { gsap, ScrollToPlugin, ScrollTrigger } from 'gsap/all'
import { throttle } from 'throttle-debounce'
import Swiper from 'swiper'
import { Navigation } from 'swiper/modules'

import { app } from '../../appState'
import DeviceCheck from '../../utils/deviceCheck'
import { shouldRefreshScrollLayout } from '../../utils/viewportHeight'
import initTopMovieArea from '../topMovieArea'

/** Swiper の `breakpoints.768` と揃える（767px 以下がスマホ） */
const FEATURE_VIEWPORT_MOBILE_MAX = 767
const FEATURE_ANCHOR_OFFSET_PC = 0
/** 各スライド区間のうち、reveal 完了後に「全表示」をキープする割合（0.15 = 後半15%をホールド） */
const FEATURE_CLIP_REVEAL_HOLD_RATIO = 0.25

function getLenis() {
  return app.Root?.lenis ?? null
}

function isFeaturesMobileViewport() {
  return window.innerWidth <= FEATURE_VIEWPORT_MOBILE_MAX
}

function getWindowScrollY() {
  const lenis = getLenis()
  if (lenis) {
    return lenis.actualScroll
  }
  return window.scrollY ?? document.documentElement.scrollTop ?? 0
}

function getOffsetTopWithin(el, ancestor) {
  let top = 0
  let node = el
  while (node && node !== ancestor) {
    top += node.offsetTop
    node = node.offsetParent
  }
  return top
}

// Home
export default class Home {
  constructor() {
    this.DeviceCheck = new DeviceCheck()
    this.featureSlider = null
    this.featuresPinScrollTrigger = null
    this._featuresSectionBounds = null
    this._featuresPinMatchMedia = null
    this._featuresControlsBound = false
    this._featureResize = null

    // ページ読込時にスクロール復元を手動に設定
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    // ページの先頭に移動

    window.scrollTo(0, 0)

    this.init()
  }

  init() {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)
    initTopMovieArea()
    this.initKv()
    this.initFeaturesSection()
  }

  initFeaturesSection() {
    const container = document.querySelector('.js-feature-slider')
    if (!container) {
      return
    }

    this.initFeatureSwiper(container)

    // Lenis は setCommon の末尾で初期化されるため、同一ターンより後にバインドする
    setTimeout(() => {
      this.initFeaturesPinScroll()
      this.setupFeaturePaginationAndPcScroll(container)
    }, 0)
  }

  /** trigger の start〜end をスクロール座標で 0〜1 に変換（通過後は 1 にクランプ。st.progress は非アクティブ時に不安定なことがある） */
  getFeaturesPinProgress(st) {
    if (!st) {
      return 0
    }
    const start = st.start
    const end = st.end
    if (typeof start !== 'number' || typeof end !== 'number' || end <= start) {
      return 0
    }
    const y = getWindowScrollY()
    return gsap.utils.clamp(0, 1, (y - start) / (end - start))
  }

  /**
   * .js-features-content-section の DOM 位置から pin 区間内の進捗境界 [0, …, 1] を算出
   */
  computeFeaturesSectionProgressBounds(content, sections, st) {
    const n = sections.length
    if (!content || !st || n < 2) {
      return n > 0 ? sections.map((_, i) => i / Math.max(n - 1, 1)) : [0, 1]
    }

    const start = st.start
    const range = st.end - start
    if (typeof start !== 'number' || range <= 0) {
      return sections.map((_, i) => i / (n - 1))
    }

    const bounds = sections.map((section) => {
      const top = getOffsetTopWithin(section, content)
      return gsap.utils.clamp(0, 1, top / range)
    })

    bounds[0] = 0
    bounds[n - 1] = 1
    for (let i = 1; i < n; i++) {
      bounds[i] = Math.max(bounds[i], bounds[i - 1])
    }

    return bounds
  }

  getFeaturesActiveIndexFromProgress(progress, bounds) {
    if (!bounds?.length) {
      return 0
    }
    const p = gsap.utils.clamp(0, 1, progress)
    let active = 0
    for (let i = 0; i < bounds.length; i++) {
      if (p >= bounds[i] - 1e-6) {
        active = i
      }
    }
    return Math.min(active, bounds.length - 1)
  }

  updateFeaturesPinVisuals(items, progress) {
    const bounds = this._featuresSectionBounds
    this.applyFeaturesItemsClip(items, progress, bounds)
    this.setFeaturePaginationActive(this.getFeaturesActiveIndexFromProgress(progress, bounds))
  }

  applyFeaturesItemsClip(items, progress, bounds) {
    const n = items.length
    const p = gsap.utils.clamp(0, 1, progress)

    for (let i = 0; i < n; i++) {
      const el = items[i]
      if (i === 0) {
        el.style.clipPath = 'none'
        continue
      }

      let segStart
      let segEnd
      if (bounds?.length >= 2 && bounds[i] != null && bounds[i - 1] != null) {
        segStart = bounds[i - 1]
        segEnd = bounds[i]
      } else {
        segStart = i / n
        segEnd = (i + 1) / n
      }

      const segLen = segEnd - segStart
      const revealEnd = segLen > 0 ? segStart + segLen * (1 - FEATURE_CLIP_REVEAL_HOLD_RATIO) : segEnd

      let insetTop = 100
      if (p >= revealEnd) {
        insetTop = 0
      } else if (p > segStart && revealEnd > segStart) {
        const revealProgress = (p - segStart) / (revealEnd - segStart)
        insetTop = (1 - revealProgress) * 100
      }
      el.style.clipPath = `inset(${insetTop}% 0 0 0)`
    }
  }

  /**
   * PC: .js-features-content を trigger に、wrapper は CSS sticky（top:0）。
   * clip-path は ScrollTrigger（scrub）＋ section の DOM 位置に基づく境界で更新。
   */
  initFeaturesPinScroll() {
    const content = document.querySelector('.js-features-content')
    const wrapper = document.querySelector('.js-features-content-wrapper')
    const items = [...document.querySelectorAll('.js-features-content-item')]
    const sections = [...document.querySelectorAll('.js-features-content-section')]
    if (!content || !wrapper || items.length === 0) {
      return
    }

    this._featuresPinMatchMedia?.revert()
    this._featuresPinMatchMedia = ScrollTrigger.matchMedia({
      [`(min-width: ${FEATURE_VIEWPORT_MOBILE_MAX + 1}px)`]: () => {
        const n = items.length

        const refreshSectionBounds = (trigger) => {
          this._featuresSectionBounds = this.computeFeaturesSectionProgressBounds(content, sections, trigger)
        }

        const st = ScrollTrigger.create({
          id: 'features-content-scrub',
          trigger: content,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1,
          invalidateOnRefresh: true,
          onRefresh: (self) => {
            refreshSectionBounds(self)
          },
          onUpdate: (self) => {
            if (wrapper.classList.contains('is-features-reveal-complete')) {
              this.applyFeaturesItemsClip(items, 1, this._featuresSectionBounds)
              this.setFeaturePaginationActive(n - 1)
              return
            }
            this.updateFeaturesPinVisuals(items, this.getFeaturesPinProgress(self))
          },
          onEnter: () => {
            document.body.classList.add('is-features-pinned')
          },
          onLeave: () => {
            document.body.classList.remove('is-features-pinned')
            wrapper.classList.add('is-features-reveal-complete')
            this.applyFeaturesItemsClip(items, 1, this._featuresSectionBounds)
            this.setFeaturePaginationActive(n - 1)
          },
          onEnterBack: () => {
            document.body.classList.add('is-features-pinned')
            wrapper.classList.remove('is-features-reveal-complete')
          },
          onLeaveBack: () => {
            document.body.classList.remove('is-features-pinned')
            wrapper.classList.remove('is-features-reveal-complete')
          },
        })

        this.featuresPinScrollTrigger = st
        refreshSectionBounds(st)
        this.updateFeaturesPinVisuals(items, this.getFeaturesPinProgress(st))

        return () => {
          document.body.classList.remove('is-features-pinned')
          wrapper.classList.remove('is-features-reveal-complete')
          this._featuresSectionBounds = null
          items.forEach((el) => {
            el.style.removeProperty('clip-path')
          })
          st.kill()
          if (this.featuresPinScrollTrigger === st) {
            this.featuresPinScrollTrigger = null
          }
        }
      },
    })
  }

  initFeatureSwiper(container) {
    const prevBtn = document.querySelector('.js-feature-slider-prev .p-top-features__arrow-item-link')
    const nextBtn = document.querySelector('.js-feature-slider-next .p-top-features__arrow-item-link')

    const preventNav = (e) => {
      e.preventDefault()
    }
    if (prevBtn) {
      prevBtn.addEventListener('click', preventNav)
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', preventNav)
    }

    this.featureSlider = new Swiper(container, {
      modules: [Navigation],
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 600,
      watchOverflow: true,
      navigation: {
        prevEl: prevBtn || '.js-feature-slider-prev .p-top-features__arrow-item-link',
        nextEl: nextBtn || '.js-feature-slider-next .p-top-features__arrow-item-link',
      },
      breakpoints: {
        768: {
          enabled: false,
        },
      },
      on: {
        slideChange: (swiper) => {
          if (!isFeaturesMobileViewport()) {
            return
          }
          this.setFeaturePaginationActive(swiper.activeIndex)
        },
      },
    })
  }

  setFeaturePaginationActive(activeIndex) {
    const links = document.querySelectorAll('.js-feature-slider-pagination .p-top-features__side-nav-item-link')
    links.forEach((link, i) => {
      link.classList.toggle('is-current', i === activeIndex)
    })
  }

  syncFeaturesPinVisualsAfterScroll() {
    const st = this.featuresPinScrollTrigger
    if (!st) {
      return
    }
    ScrollTrigger.refresh()
    const items = [...document.querySelectorAll('.js-features-content-item')]
    if (items.length) {
      this.updateFeaturesPinVisuals(items, this.getFeaturesPinProgress(st))
    }
  }

  /** PC: サイドナビの href（#features01 等）で指定されたアンカーへスクロール */
  scrollFeaturesToSectionPc(hash) {
    if (!hash || !hash.startsWith('#')) {
      return
    }

    const el = document.querySelector(hash)
    if (!el) {
      return
    }

    const lenis = getLenis()
    const scrollOptions = {
      duration: 0.85,
      lock: true,
      programmatic: true,
      offset: -FEATURE_ANCHOR_OFFSET_PC,
      onComplete: () => {
        this.syncFeaturesPinVisualsAfterScroll()
      },
    }

    gsap.killTweensOf(window)

    if (lenis) {
      lenis.scrollTo(el, scrollOptions)
      return
    }

    const y = el.getBoundingClientRect().top + getWindowScrollY() - FEATURE_ANCHOR_OFFSET_PC
    gsap.to(window, {
      duration: 0.8,
      scrollTo: { y: Math.max(0, y), autoKill: true },
      ease: 'power2.inOut',
      onComplete: () => {
        this.syncFeaturesPinVisualsAfterScroll()
      },
    })
  }

  setupFeaturePaginationAndPcScroll(container) {
    if (this._featuresControlsBound) {
      return
    }
    this._featuresControlsBound = true

    const paginationItems = document.querySelectorAll('.js-feature-slider-pagination-item')

    paginationItems.forEach((item, index) => {
      const link = item.querySelector('a[href^="#"]')
      if (!link) {
        return
      }
      link.addEventListener('click', (e) => {
        e.preventDefault()
        if (isFeaturesMobileViewport()) {
          this.featureSlider?.slideTo(index)
          this.setFeaturePaginationActive(index)
        } else {
          const hash = link.getAttribute('href')
          this.setFeaturePaginationActive(index)
          this.scrollFeaturesToSectionPc(hash)
        }
      })
    })

    this._featureResize = throttle(150, () => {
      this.featureSlider?.update()
      if (!shouldRefreshScrollLayout()) {
        return
      }
      ScrollTrigger.refresh()
      if (isFeaturesMobileViewport()) {
        this.setFeaturePaginationActive(this.featureSlider?.activeIndex ?? 0)
      } else if (this.featuresPinScrollTrigger) {
        const st = this.featuresPinScrollTrigger
        const wrapper = document.querySelector('.js-features-content-wrapper')
        const items = [...container.querySelectorAll('.js-features-content-item')]
        const n = items.length
        if (wrapper?.classList.contains('is-features-reveal-complete')) {
          this.applyFeaturesItemsClip(items, 1, this._featuresSectionBounds)
          this.setFeaturePaginationActive(n - 1)
        } else {
          const p = this.getFeaturesPinProgress(st)
          this.updateFeaturesPinVisuals(items, p)
        }
      }
    })
    window.addEventListener('resize', this._featureResize)

    if (isFeaturesMobileViewport()) {
      this.setFeaturePaginationActive(this.featureSlider?.activeIndex ?? 0)
    }
  }
  initKv() {
    // KV Video
    const videoWrap = document.querySelectorAll('.js-kv-video')
    const kvVideos = document.querySelectorAll('.js-kv-video-item')

    // barba 再遷移後は autoplay が発火しないケースがあるため、明示的に再生を試みる
    const ensureKvVideoPlayback = (video) => {
      if (!video) {
        return
      }
      video.muted = true
      video.playsInline = true
      video.autoplay = true

      const retryPlay = () => {
        video.play().catch(() => {})
      }

      video.play().catch(() => {
        video.addEventListener('loadeddata', retryPlay, { once: true })
        video.addEventListener('canplay', retryPlay, { once: true })
      })
    }

    if (kvVideos.length) {
      requestAnimationFrame(() => {
        kvVideos.forEach((video) => ensureKvVideoPlayback(video))
      })
    }

    gsap.set(videoWrap, { y: 200, autoAlpha: 0 })
    gsap.to(videoWrap, {
      autoAlpha: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
    })

    const kvTop = document.querySelector('.js-kv-top')
    const kvVideoOverlay = document.querySelector('.js-kv-video-overlay')
    const textBlocks = document.querySelectorAll('.js-kv-text-anim')
    const edgeTop = document.querySelector('.js-kv-border-edge--top')
    const edgeRight = document.querySelector('.js-kv-border-edge--right')
    const edgeBottom = document.querySelector('.js-kv-border-edge--bottom')
    const edgeLeft = document.querySelector('.js-kv-border-edge--left')
    const borderEdges = [edgeTop, edgeRight, edgeBottom, edgeLeft].filter(Boolean)

    // KV 動画オーバーレイ（スクロールで opacity 0 → 0.4）
    if (kvTop && kvVideoOverlay) {
      gsap.set(kvVideoOverlay, { opacity: 0 })
      gsap.to(kvVideoOverlay, {
        opacity: 0.6,
        ease: 'none',
        scrollTrigger: {
          trigger: kvTop,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      })
    }

    // KV 動画パララックス
    gsap.to('.js-kv-video-item', {
      y: '30%',
      scrollTrigger: {
        trigger: '.js-kv-lead',
        start: 'bottom bottom',
        end: 'bottom top',
        scrub: true,
      },
    })

    // KV オープニング（テキスト + ボーダー）／スクロールで非表示・戻りで再表示
    if (!kvTop || !textBlocks.length) {
      return
    }

    const itemsFirst = textBlocks[0].querySelectorAll('.js-kv-text-anim-item')
    const itemsSecond = textBlocks[1] ? textBlocks[1].querySelectorAll('.js-kv-text-anim-item') : []
    const textItems = gsap.utils.toArray('.js-kv-text-anim-item')

    let kvBorderFullyDrawn = false

    const TEXT_STAGGER = 0.05
    const SECOND_BLOCK_DELAY = 0.1
    const BORDER_DRAW_DURATION = 0.8
    const TEXT_FADE_DURATION = 1
    const HIDE_DURATION = 0.45
    const BORDER_HIDE_DURATION = 0.6

    const halfDraw = BORDER_DRAW_DURATION / 2
    const halfHide = BORDER_HIDE_DURATION / 2
    const borderEase = 'power2.out'

    gsap.set(textItems, { opacity: 0 })
    if (borderEdges.length === 4) {
      gsap.set(edgeTop, { scaleX: 0 })
      gsap.set(edgeRight, { scaleY: 0 })
      gsap.set(edgeBottom, { scaleX: 0 })
      gsap.set(edgeLeft, { scaleY: 0 })
    }

    const applyKvBorderDrawState = () => {
      if (borderEdges.length !== 4) {
        return
      }
      if (kvBorderFullyDrawn) {
        gsap.set(edgeTop, { scaleX: 1 })
        gsap.set(edgeRight, { scaleY: 1 })
        gsap.set(edgeBottom, { scaleX: 1 })
        gsap.set(edgeLeft, { scaleY: 1 })
      } else {
        gsap.set(edgeTop, { scaleX: 0 })
        gsap.set(edgeRight, { scaleY: 0 })
        gsap.set(edgeBottom, { scaleX: 0 })
        gsap.set(edgeLeft, { scaleY: 0 })
      }
    }

    const fixKvBorderAfterResize = () => {
      if (borderEdges.length !== 4) {
        return
      }
      const animating = borderEdges.some((el) => gsap.isTweening(el))
      if (animating) {
        return
      }
      if (shouldRefreshScrollLayout()) {
        ScrollTrigger.refresh()
      }
      applyKvBorderDrawState()
    }

    const playKvOpening = () => {
      gsap.killTweensOf([...textItems, ...borderEdges])
      gsap.set(textItems, { opacity: 0 })
      if (borderEdges.length === 4) {
        gsap.set(edgeTop, { scaleX: 0 })
        gsap.set(edgeRight, { scaleY: 0 })
        gsap.set(edgeBottom, { scaleX: 0 })
        gsap.set(edgeLeft, { scaleY: 0 })
      }

      const tl = gsap.timeline({ defaults: { overwrite: 'auto' } })

      if (borderEdges.length === 4) {
        const pathFromTl = gsap.timeline({
          onComplete: () => {
            kvBorderFullyDrawn = true
          },
        })
        pathFromTl.to(edgeTop, { scaleX: 1, duration: halfDraw, ease: borderEase })
        pathFromTl.to(edgeRight, { scaleY: 1, duration: halfDraw, ease: borderEase })
        const pathFromBr = gsap.timeline()
        pathFromBr.to(edgeBottom, { scaleX: 1, duration: halfDraw, ease: borderEase })
        pathFromBr.to(edgeLeft, { scaleY: 1, duration: halfDraw, ease: borderEase })
        tl.add(pathFromTl, 0)
        tl.add(pathFromBr, 0)
      }

      tl.to(
        itemsFirst,
        {
          opacity: 1,
          duration: TEXT_FADE_DURATION,
          stagger: TEXT_STAGGER,
          ease: 'power2.out',
          delay: 0.5,
        },
        0,
      )
      if (itemsSecond.length) {
        tl.to(
          itemsSecond,
          {
            opacity: 1,
            duration: TEXT_FADE_DURATION,
            stagger: TEXT_STAGGER,
            ease: 'power2.out',
            delay: 0.5,
          },
          SECOND_BLOCK_DELAY,
        )
      }
      return tl
    }

    const playKvHide = () => {
      gsap.killTweensOf([...textItems, ...borderEdges])
      const tl = gsap.timeline({ defaults: { overwrite: 'auto' } })
      tl.to(
        textItems,
        {
          opacity: 0,
          duration: HIDE_DURATION,
          ease: 'power2.in',
        },
        0,
      )

      if (borderEdges.length === 4) {
        const hideTrFirst = gsap.timeline({
          onComplete: () => {
            kvBorderFullyDrawn = false
          },
        })
        hideTrFirst.to(edgeRight, { scaleY: 0, duration: halfHide, ease: borderEase })
        hideTrFirst.to(edgeTop, { scaleX: 0, duration: halfHide, ease: borderEase })
        const hideBlFirst = gsap.timeline()
        hideBlFirst.to(edgeLeft, { scaleY: 0, duration: halfHide, ease: borderEase })
        hideBlFirst.to(edgeBottom, { scaleX: 0, duration: halfHide, ease: borderEase })
        tl.add(hideTrFirst, 0)
        tl.add(hideBlFirst, 0)
      } else {
        kvBorderFullyDrawn = false
      }

      return tl
    }

    requestAnimationFrame(() => {
      kvBorderFullyDrawn = false
      playKvOpening()
      ScrollTrigger.refresh()
    })

    window.addEventListener(
      'resize',
      throttle(150, () => {
        fixKvBorderAfterResize()
      }),
    )

    ScrollTrigger.create({
      trigger: kvTop,
      start: 'top top',
      onEnter: () => {
        playKvHide()
      },
      onLeaveBack: () => {
        playKvOpening()
      },
    })
  }
}
